(() => {
  'use strict';

  const CHECKOUT_VERSION = 1;
  const OFFICE_MIN_QUERY = 2;
  const OFFICE_DEBOUNCE_MS = 180;
  const OFFICE_LIMIT = 30;
  const CITY_MIN_QUERY = 2;
  const CITY_DEBOUNCE_MS = 180;
  const CITY_LIMIT = 30;
  const STREET_MIN_QUERY = 2;
  const STREET_DEBOUNCE_MS = 180;
  const STREET_LIMIT = 30;

  const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const initAllCheckouts = (scope = document) => {
    const roots = [];
    if (scope.matches?.('.cp-checkout[data-section-id]')) roots.push(scope);
    scope.querySelectorAll?.('.cp-checkout[data-section-id]').forEach((root) => roots.push(root));
    roots.forEach(initCheckout);
  };

  document.addEventListener('DOMContentLoaded', () => initAllCheckouts(document));
  document.addEventListener('carposter:checkout-mounted', (event) => {
    initAllCheckouts(event.detail?.root || document);
  });

  window.CarposterCheckout = window.CarposterCheckout || {};
  window.CarposterCheckout.init = initAllCheckouts;

  function initCheckout(root) {
    if (root.dataset.cpCheckoutInitialized === 'true') return;
    root.dataset.cpCheckoutInitialized = 'true';

    if (root.closest('.cp-checkout-modal')) {
      root.classList.add('cp-checkout--embedded');
    }

    const sectionId = root.dataset.sectionId;
    const configEl = document.getElementById(`CarposterCheckoutConfig-${sectionId}`);
    const form = root.querySelector('.cp-checkout-form');

    if (!configEl || !form) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[CarPoster checkout] Invalid checkout configuration.', error);
      return;
    }

    const state = {
      deliveryType: 'OFFICE',
      selectedOffice: null,
      officeLookupUnavailable: false,
      officeSearchController: null,
      officeSearchTimer: null,
      devOffices: null,
      devOfficesPromise: null,
      fallbackOffices: null,
      fallbackOfficesPromise: null,
      officeFallbackActive: false,
      selectedCity: null,
      cityLookupUnavailable: false,
      citySearchController: null,
      citySearchTimer: null,
      selectedStreet: null,
      streetSearchController: null,
      streetSearchTimer: null,
      submitting: false,
      discount: null,
      discountApplying: false,
      checkoutId: getOrCreateCheckoutId(config),
    };

    const elements = {
      deliveryOptions: [...root.querySelectorAll('[data-delivery-option]')],
      deliveryPanels: [...root.querySelectorAll('[data-delivery-panel]')],
      officeSearch: root.querySelector('[data-office-search]'),
      officeSearchWrap: root.querySelector('[data-office-search-wrap]'),
      officeResults: root.querySelector('[data-office-results]'),
      officeSpinner: root.querySelector('[data-office-spinner]'),
      selectedOffice: root.querySelector('[data-selected-office]'),
      selectedOfficeName: root.querySelector('[data-selected-office-name]'),
      selectedOfficeAddress: root.querySelector('[data-selected-office-address]'),
      changeOffice: root.querySelector('[data-change-office]'),
      citySearch: root.querySelector('[data-city-search]'),
      citySearchWrap: root.querySelector('[data-city-search-wrap]'),
      cityResults: root.querySelector('[data-city-results]'),
      citySpinner: root.querySelector('[data-city-spinner]'),
      selectedCity: root.querySelector('[data-selected-city]'),
      selectedCityName: root.querySelector('[data-selected-city-name]'),
      changeCity: root.querySelector('[data-change-city]'),
      streetSearch: root.querySelector('[data-street-search]'),
      streetResults: root.querySelector('[data-street-results]'),
      streetSpinner: root.querySelector('[data-street-spinner]'),
      shipping: [...root.querySelectorAll('[data-cp-shipping]')],
      totals: [...root.querySelectorAll('[data-cp-total]')],
      totalsBgn: [...root.querySelectorAll('[data-cp-total-bgn]')],
      discountToggles: [...root.querySelectorAll('[data-discount-toggle]')],
      discountForms: [...root.querySelectorAll('[data-discount-form]')],
      discountInputs: [...root.querySelectorAll('[data-discount-input]')],
      discountApplyButtons: [...root.querySelectorAll('[data-discount-apply]')],
      discountMessages: [...root.querySelectorAll('[data-discount-message]')],
      discountRows: [...root.querySelectorAll('[data-cp-discount-row]')],
      discountLabels: [...root.querySelectorAll('[data-cp-discount-label]')],
      discountAmounts: [...root.querySelectorAll('[data-cp-discount-amount]')],
      discountRemoveButtons: [...root.querySelectorAll('[data-discount-remove]')],
      mobileCartDetails: root.querySelector('.cp-mobile-cart-preview__details'),
      submit: root.querySelector('[data-submit-order]'),
      submitLabel: root.querySelector('[data-submit-label]'),
      submitSpinner: root.querySelector('[data-submit-spinner]'),
      formError: root.querySelector('[data-form-error]'),
    };

    bindDeliveryOptions();
    bindOfficeSearch();
    bindCitySearch();
    bindStreetSearch();
    bindDiscount();
    bindItemEditing();
    bindForm();
    updatePriceDisplay();


    function bindDeliveryOptions() {
      elements.deliveryOptions.forEach((option) => {
        const input = option.querySelector('input[type="radio"]');
        if (!input) return;

        input.addEventListener('change', () => {
          if (!input.checked) return;
          state.deliveryType = input.value;

          elements.deliveryOptions.forEach((item) => {
            item.classList.toggle('is-selected', item === option);
          });

          elements.deliveryPanels.forEach((panel) => {
            panel.hidden = panel.dataset.deliveryPanel !== state.deliveryType;
          });

          const courierTitle = root.querySelector('[data-courier-title]');
          if (courierTitle) courierTitle.textContent = state.deliveryType === 'ADDRESS' ? 'Доставка с Еконт' : 'Офис на Еконт';

          clearFormError();

          // A CarPoster free-shipping code belongs to the checkout session,
          // but its benefit applies only to Econt OFFICE delivery. Keep the code
          // remembered when the customer switches to ADDRESS; simply stop applying
          // the shipping benefit. If they switch back to OFFICE, the same code is
          // revalidated and becomes active again automatically.
          updatePriceDisplay();
          renderAppliedDiscount();
          if (state.discount && !(state.deliveryType === 'ADDRESS' && state.discount.kind === 'FREE_SHIPPING')) {
            refreshAppliedDiscount();
          }
        });
      });
    }

    function bindOfficeSearch() {
      if (!elements.officeSearch) return;

      elements.officeSearch.addEventListener('input', () => {
        state.selectedOffice = null;
        hideSelectedOffice();
        clearFieldError('office');

        window.clearTimeout(state.officeSearchTimer);
        const query = elements.officeSearch.value.trim();

        if (query.length < OFFICE_MIN_QUERY) {
          hideOfficeResults();
          return;
        }

        state.officeSearchTimer = window.setTimeout(() => {
          if (state.officeFallbackActive) searchFallbackOffices(query);
          else searchOffices(query);
        }, OFFICE_DEBOUNCE_MS);
      });

      elements.officeSearch.addEventListener('focus', () => {
        const query = elements.officeSearch.value.trim();
        if (query.length >= OFFICE_MIN_QUERY && !state.selectedOffice) {
          if (state.officeFallbackActive) searchFallbackOffices(query);
          else searchOffices(query);
        }
      });

      elements.officeSearch.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') hideOfficeResults();
      });

      elements.changeOffice?.addEventListener('click', () => {
        state.selectedOffice = null;
        hideSelectedOffice();
        elements.officeSearch.value = '';
        elements.officeSearchWrap.hidden = false;
        elements.officeSearch.focus();
      });

      document.addEventListener('click', (event) => {
        if (!elements.officeResults || elements.officeResults.hidden) return;
        if (elements.officeResults.contains(event.target) || elements.officeSearchWrap?.contains(event.target)) return;
        hideOfficeResults();
      });
    }

    async function searchOffices(query) {
      if (!config.apiBaseUrl) {
        await searchFallbackOffices(query);
        return;
      }

      state.officeSearchController?.abort();
      state.officeSearchController = new AbortController();
      const controller = state.officeSearchController;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        try { controller.abort(); } catch (_) {}
      }, 2800);
      setOfficeLoading(true);

      try {
        const base = config.apiBaseUrl.replace(/\/$/, '');
        const response = await fetch(`${base}/checkout/econt/offices?q=${encodeURIComponent(query)}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'omit',
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Office search failed (${response.status})`);
        }

        const payload = await response.json();
        const offices = normalizeOfficeResponse(payload)
          .filter(isSelectableOffice)
          .slice(0, OFFICE_LIMIT);

        state.officeLookupUnavailable = false;
        state.officeFallbackActive = false;
        elements.officeSearch?.removeAttribute('data-manual-fallback');
        elements.officeSearch?.removeAttribute('data-local-fallback');
        renderOfficeResults(offices);
      } catch (error) {
        if (error.name === 'AbortError' && !timedOut) return;
        console.error('[CarPoster checkout] Office API search error. Switching to bundled office list.', error);
        state.officeLookupUnavailable = true;
        const fallbackWorked = await searchFallbackOffices(query, { keepSpinner: true });
        if (!fallbackWorked) {
          state.officeFallbackActive = false;
          elements.officeSearch?.setAttribute('data-manual-fallback', 'true');
          showOfficeMessage('Списъкът с офиси временно не е достъпен. Въведете град и офис на Еконт ръчно.');
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (state.officeSearchController === controller) state.officeSearchController = null;
        setOfficeLoading(false);
      }
    }

    async function searchFallbackOffices(query, { keepSpinner = false } = {}) {
      if (!config.fallbackOfficeDataUrl) return false;
      if (!keepSpinner) setOfficeLoading(true);
      try {
        const offices = await loadFallbackOffices();
        const q = normalizeSearchText(query);
        const matches = offices
          .filter(isSelectableOffice)
          .map((office) => ({ office, score: scoreOffice(office, q) }))
          .filter((entry) => entry.score >= 0)
          .sort((a, b) => b.score - a.score || a.office.name.localeCompare(b.office.name, 'bg'))
          .slice(0, OFFICE_LIMIT)
          .map((entry) => ({ ...entry.office, source: 'LOCAL_FALLBACK' }));

        state.officeFallbackActive = true;
        state.officeLookupUnavailable = true;
        elements.officeSearch?.removeAttribute('data-manual-fallback');
        elements.officeSearch?.setAttribute('data-local-fallback', 'true');
        renderOfficeResults(matches);
        return true;
      } catch (error) {
        console.error('[CarPoster checkout] Bundled office list error.', error);
        return false;
      } finally {
        if (!keepSpinner) setOfficeLoading(false);
      }
    }

    async function loadFallbackOffices() {
      if (state.fallbackOffices) return state.fallbackOffices;
      if (state.fallbackOfficesPromise) return state.fallbackOfficesPromise;

      state.fallbackOfficesPromise = fetch(config.fallbackOfficeDataUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        cache: 'force-cache'
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Bundled office data failed (${response.status})`);
          return response.json();
        })
        .then((payload) => normalizeOfficeResponse(payload))
        .then((offices) => {
          state.fallbackOffices = offices;
          return offices;
        })
        .finally(() => {
          state.fallbackOfficesPromise = null;
        });

      return state.fallbackOfficesPromise;
    }

    async function searchDevOffices(query) {
      if (!config.devOfficeDataUrl) {
        showOfficeMessage('Офисите ще бъдат налични след свързването на checkout backend-а.');
        return;
      }

      setOfficeLoading(true);
      try {
        const offices = await loadDevOffices();
        const q = normalizeSearchText(query);
        const matches = offices
          .filter(isSelectableOffice)
          .map((office) => ({ office, score: scoreOffice(office, q) }))
          .filter((entry) => entry.score >= 0)
          .sort((a, b) => b.score - a.score || a.office.name.localeCompare(b.office.name, 'bg'))
          .slice(0, OFFICE_LIMIT)
          .map((entry) => entry.office);

        renderOfficeResults(matches);
      } catch (error) {
        console.error('[CarPoster checkout] Development office data error.', error);
        showOfficeMessage('Не успяхме да заредим тестовия списък с офиси.');
      } finally {
        setOfficeLoading(false);
      }
    }

    async function loadDevOffices() {
      if (state.devOffices) return state.devOffices;
      if (state.devOfficesPromise) return state.devOfficesPromise;

      state.devOfficesPromise = fetch(config.devOfficeDataUrl, { headers: { 'Accept': 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Development office data failed (${response.status})`);
          return response.json();
        })
        .then((payload) => normalizeOfficeResponse(payload))
        .then((offices) => {
          state.devOffices = offices;
          return offices;
        })
        .finally(() => {
          state.devOfficesPromise = null;
        });

      return state.devOfficesPromise;
    }

    function normalizeOfficeResponse(payload) {
      const items = Array.isArray(payload) ? payload : (payload.results || payload.offices || payload.items || payload.data || []);
      if (!Array.isArray(items)) return [];

      return items.map((office) => {
        const addressObject = office.address && typeof office.address === 'object' ? office.address : {};
        const city = office.city || addressObject.city?.name || addressObject.cityName || '';
        const address = typeof office.address === 'string'
          ? office.address
          : office.fullAddress || addressObject.fullAddress || addressObject.fullAddressEn || addressObject.street || '';

        return {
          id: String(office.id ?? office.officeId ?? ''),
          code: String(office.code ?? office.officeCode ?? ''),
          name: office.name || office.nameEn || office.officeName || 'Офис на Еконт',
          city,
          address,
          postalCode: String(office.postalCode || addressObject.city?.postCode || addressObject.postalCode || ''),
          latitude: office.latitude ?? office.coordinates?.latitude ?? null,
          longitude: office.longitude ?? office.coordinates?.longitude ?? null,
          isAPS: Boolean(office.isAPS ?? office.aps ?? false),
          isMPS: Boolean(office.isMPS ?? office.mps ?? false),
          source: String(office.source || ''),
        };
      }).filter((office) => office.id || office.code);
    }

    function isSelectableOffice(office) {
      return !office.isAPS && !office.isMPS;
    }

    function scoreOffice(office, normalizedQuery) {
      if (!normalizedQuery) return -1;
      const name = normalizeSearchText(office.name);
      const city = normalizeSearchText(office.city);
      const address = normalizeSearchText(office.address);
      const code = normalizeSearchText(office.code);
      const postalCode = normalizeSearchText(office.postalCode);
      const haystack = `${city} ${name} ${address} ${code} ${postalCode}`.trim();

      if (!haystack.includes(normalizedQuery)) return -1;
      if (name === normalizedQuery || city === normalizedQuery || code === normalizedQuery) return 100;
      if (name.startsWith(normalizedQuery) || city.startsWith(normalizedQuery)) return 80;
      if (`${city} ${name}`.startsWith(normalizedQuery)) return 75;
      if (name.includes(normalizedQuery)) return 60;
      if (address.includes(normalizedQuery)) return 30;
      return 20;
    }

    function renderOfficeResults(offices) {
      if (!elements.officeResults) return;
      elements.officeResults.innerHTML = '';

      if (!offices.length) {
        showOfficeMessage('Не намерихме офис. Опитайте с град, квартал, улица или име на офис.');
        return;
      }

      offices.forEach((office) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cp-office-result';
        button.setAttribute('role', 'option');

        const main = document.createElement('span');
        main.className = 'cp-office-result__main';

        const name = document.createElement('strong');
        name.textContent = office.city ? `${office.city} — ${office.name}` : office.name;

        const address = document.createElement('span');
        address.textContent = office.address || 'Адрес на офиса';

        main.append(name, address);

        const code = document.createElement('span');
        code.className = 'cp-office-result__code';
        code.textContent = office.code ? `#${office.code}` : '';

        button.append(main, code);
        button.addEventListener('click', () => selectOffice(office));
        elements.officeResults.append(button);
      });

      elements.officeResults.hidden = false;
      elements.officeSearch?.setAttribute('aria-expanded', 'true');
    }

    function selectOffice(office) {
      state.selectedOffice = office;
      elements.officeSearch.value = '';
      elements.officeSearchWrap.hidden = true;
      elements.selectedOfficeName.textContent = office.city ? `${office.city} — ${office.name}` : office.name;
      elements.selectedOfficeAddress.textContent = office.address || `Офис код ${office.code}`;
      elements.selectedOffice.hidden = false;
      hideOfficeResults();
      clearFieldError('office');
    }

    function hideSelectedOffice() {
      if (elements.selectedOffice) elements.selectedOffice.hidden = true;
      if (elements.officeSearchWrap) elements.officeSearchWrap.hidden = false;
    }

    function showOfficeMessage(message) {
      if (!elements.officeResults) return;
      elements.officeResults.innerHTML = '';
      const element = document.createElement('div');
      element.className = 'cp-office-results__message';
      element.textContent = message;
      elements.officeResults.append(element);
      elements.officeResults.hidden = false;
      elements.officeSearch?.setAttribute('aria-expanded', 'true');
    }

    function hideOfficeResults() {
      if (elements.officeResults) elements.officeResults.hidden = true;
      elements.officeSearch?.setAttribute('aria-expanded', 'false');
    }

    function setOfficeLoading(loading) {
      if (elements.officeSpinner) elements.officeSpinner.hidden = !loading;
    }

    function bindCitySearch() {
      if (!elements.citySearch) return;

      elements.citySearch.addEventListener('input', () => {
        state.selectedCity = null;
        clearStreetForCityChange();
        hideSelectedCity();
        clearFieldError('city');

        window.clearTimeout(state.citySearchTimer);
        const query = elements.citySearch.value.trim();

        if (query.length < CITY_MIN_QUERY) {
          hideCityResults();
          return;
        }

        if (!state.cityLookupUnavailable) {
          state.citySearchTimer = window.setTimeout(() => searchCities(query), CITY_DEBOUNCE_MS);
        } else {
          showCityMessage('Списъкът с населени места временно не е достъпен. Въведете населеното място ръчно.');
        }
      });

      elements.citySearch.addEventListener('focus', () => {
        const query = elements.citySearch.value.trim();
        if (query.length >= CITY_MIN_QUERY && !state.selectedCity && !state.cityLookupUnavailable) searchCities(query);
      });

      elements.citySearch.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') hideCityResults();
      });

      elements.changeCity?.addEventListener('click', () => {
        state.selectedCity = null;
        clearStreetForCityChange();
        hideSelectedCity();
        elements.citySearch.value = '';
        elements.citySearchWrap.hidden = false;
        elements.citySearch.focus();
      });

      document.addEventListener('click', (event) => {
        if (!elements.cityResults || elements.cityResults.hidden) return;
        if (elements.cityResults.contains(event.target) || elements.citySearchWrap?.contains(event.target)) return;
        hideCityResults();
      });
    }

    async function searchCities(query) {
      if (!config.apiBaseUrl) {
        showCityMessage('Населените места ще бъдат налични след свързването на checkout backend-а.');
        return;
      }

      state.citySearchController?.abort();
      state.citySearchController = new AbortController();
      setCityLoading(true);

      try {
        const base = config.apiBaseUrl.replace(/\/$/, '');
        const response = await fetch(`${base}/checkout/econt/cities?q=${encodeURIComponent(query)}`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'omit',
          signal: state.citySearchController.signal,
        });

        if (!response.ok) throw new Error(`City search failed (${response.status})`);

        const payload = await response.json();
        state.cityLookupUnavailable = false;
        elements.citySearch?.removeAttribute('data-manual-fallback');
        renderCityResults(normalizeCityResponse(payload).slice(0, CITY_LIMIT));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[CarPoster checkout] City search error.', error);
        state.cityLookupUnavailable = true;
        elements.citySearch?.setAttribute('data-manual-fallback', 'true');
        clearStreetForCityChange();
        showCityMessage('Списъкът с населени места временно не е достъпен. Въведете населеното място ръчно.');
      } finally {
        setCityLoading(false);
      }
    }

    function normalizeCityResponse(payload) {
      const items = Array.isArray(payload) ? payload : (payload.results || payload.cities || payload.items || payload.data || []);
      if (!Array.isArray(items)) return [];

      return items.map((city) => ({
        id: String(city.id ?? city.cityId ?? ''),
        name: String(city.name ?? city.city ?? ''),
        postalCode: String(city.postCode ?? city.postalCode ?? ''),
        region: String(city.regionName ?? city.region ?? city.state ?? ''),
      })).filter((city) => city.id && city.name);
    }

    function renderCityResults(cities) {
      if (!elements.cityResults) return;
      elements.cityResults.innerHTML = '';

      if (!cities.length) {
        showCityMessage('Не намерихме населено място. Проверете изписването и опитайте отново.');
        return;
      }

      cities.forEach((city) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cp-city-result';
        button.setAttribute('role', 'option');

        const main = document.createElement('span');
        main.className = 'cp-city-result__main';

        const name = document.createElement('strong');
        name.textContent = city.postalCode ? `${city.name} · ${city.postalCode}` : city.name;

        const region = document.createElement('span');
        region.textContent = city.region ? `обл. ${city.region}` : '';

        main.append(name, region);
        button.append(main);
        button.addEventListener('click', () => selectCity(city));
        elements.cityResults.append(button);
      });

      elements.cityResults.hidden = false;
      elements.citySearch?.setAttribute('aria-expanded', 'true');
    }

    function selectCity(city) {
      state.selectedCity = city;
      clearStreetForCityChange();
      elements.citySearch.value = '';
      elements.citySearchWrap.hidden = true;
      elements.selectedCityName.textContent = [city.name, city.postalCode, city.region ? `обл. ${city.region}` : ''].filter(Boolean).join(' · ');
      elements.selectedCity.hidden = false;
      hideCityResults();
      clearFieldError('city');
      prepareStreetsForCity(city.id);
    }

    function prepareStreetsForCity(cityId) {
      if (!config.apiBaseUrl || !cityId) return;
      const base = config.apiBaseUrl.replace(/\/$/, '');
      fetch(`${base}/checkout/econt/streets/prepare?cityId=${encodeURIComponent(cityId)}`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'omit',
      }).catch((error) => {
        // Street nomenclature is an enhancement, never a checkout blocker.
        console.warn('[CarPoster checkout] Street cache warmup failed.', error);
      });
    }

    function hideSelectedCity() {
      if (elements.selectedCity) elements.selectedCity.hidden = true;
      if (elements.citySearchWrap) elements.citySearchWrap.hidden = false;
    }

    function showCityMessage(message) {
      if (!elements.cityResults) return;
      elements.cityResults.innerHTML = '';
      const element = document.createElement('div');
      element.className = 'cp-city-results__message';
      element.textContent = message;
      elements.cityResults.append(element);
      elements.cityResults.hidden = false;
      elements.citySearch?.setAttribute('aria-expanded', 'true');
    }

    function hideCityResults() {
      if (elements.cityResults) elements.cityResults.hidden = true;
      elements.citySearch?.setAttribute('aria-expanded', 'false');
    }

    function setCityLoading(loading) {
      if (elements.citySpinner) elements.citySpinner.hidden = !loading;
    }

    function bindStreetSearch() {
      if (!elements.streetSearch) return;

      elements.streetSearch.addEventListener('input', () => {
        state.selectedStreet = null;
        clearFieldError('street');
        window.clearTimeout(state.streetSearchTimer);

        const query = elements.streetSearch.value.trim();
        if (state.cityLookupUnavailable || !state.selectedCity || query.length < STREET_MIN_QUERY) {
          hideStreetResults();
          return;
        }

        state.streetSearchTimer = window.setTimeout(() => searchStreets(query), STREET_DEBOUNCE_MS);
      });

      elements.streetSearch.addEventListener('focus', () => {
        const query = elements.streetSearch.value.trim();
        if (!state.cityLookupUnavailable && state.selectedCity && query.length >= STREET_MIN_QUERY) searchStreets(query);
      });

      elements.streetSearch.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') hideStreetResults();
      });

      document.addEventListener('click', (event) => {
        if (!elements.streetResults || elements.streetResults.hidden) return;
        if (elements.streetResults.contains(event.target) || elements.streetSearch?.contains(event.target)) return;
        hideStreetResults();
      });
    }

    async function searchStreets(query) {
      if (!state.selectedCity) return;

      if (!config.apiBaseUrl) {
        showStreetMessage('Улиците ще бъдат налични след свързването на checkout backend-а.');
        return;
      }

      state.streetSearchController?.abort();
      state.streetSearchController = new AbortController();
      setStreetLoading(true);

      try {
        const base = config.apiBaseUrl.replace(/\/$/, '');
        const response = await fetch(
          `${base}/checkout/econt/streets?cityId=${encodeURIComponent(state.selectedCity.id)}&q=${encodeURIComponent(query)}`,
          {
            method: 'GET',
            headers: { 'Accept': 'application/json' },
            credentials: 'omit',
            signal: state.streetSearchController.signal,
          }
        );

        if (!response.ok) throw new Error(`Street search failed (${response.status})`);

        const payload = await response.json();
        renderStreetResults(normalizeStreetResponse(payload).slice(0, STREET_LIMIT));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[CarPoster checkout] Street search error.', error);
        hideStreetResults();
      } finally {
        setStreetLoading(false);
      }
    }

    function normalizeStreetResponse(payload) {
      const items = Array.isArray(payload) ? payload : (payload.results || payload.streets || payload.items || payload.data || []);
      if (!Array.isArray(items)) return [];

      return items.map((street) => ({
        id: String(street.id ?? street.streetId ?? ''),
        cityId: String(street.cityID ?? street.cityId ?? state.selectedCity?.id ?? ''),
        name: String(street.name ?? street.street ?? ''),
      })).filter((street) => street.name);
    }

    function renderStreetResults(streets) {
      if (!elements.streetResults) return;
      elements.streetResults.innerHTML = '';

      if (!streets.length) {
        hideStreetResults();
        return;
      }

      streets.forEach((street) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cp-street-result';
        button.setAttribute('role', 'option');
        button.textContent = street.name;
        button.addEventListener('click', () => selectStreet(street));
        elements.streetResults.append(button);
      });

      elements.streetResults.hidden = false;
      elements.streetSearch?.setAttribute('aria-expanded', 'true');
    }

    function selectStreet(street) {
      state.selectedStreet = street;
      elements.streetSearch.value = street.name;
      hideStreetResults();
      clearFieldError('street');
    }

    function clearStreetForCityChange() {
      state.selectedStreet = null;
      window.clearTimeout(state.streetSearchTimer);
      state.streetSearchController?.abort();
      if (elements.streetSearch) elements.streetSearch.value = '';
      hideStreetResults();
      clearFieldError('street');
    }

    function showStreetMessage(message) {
      if (!elements.streetResults) return;
      elements.streetResults.innerHTML = '';
      const element = document.createElement('div');
      element.className = 'cp-street-results__message';
      element.textContent = message;
      elements.streetResults.append(element);
      elements.streetResults.hidden = false;
      elements.streetSearch?.setAttribute('aria-expanded', 'true');
    }

    function hideStreetResults() {
      if (elements.streetResults) elements.streetResults.hidden = true;
      elements.streetSearch?.setAttribute('aria-expanded', 'false');
    }

    function setStreetLoading(loading) {
      if (elements.streetSpinner) elements.streetSpinner.hidden = !loading;
    }


    function bindDiscount() {
      elements.discountToggles.forEach((button) => {
        button.addEventListener('click', () => {
          elements.discountForms.forEach((formEl) => { formEl.hidden = false; });
          elements.discountToggles.forEach((toggle) => { toggle.hidden = true; });
          elements.discountInputs[0]?.focus();
        });
      });

      elements.discountInputs.forEach((input, index) => {
        input.addEventListener('input', () => {
          const value = input.value;
          elements.discountInputs.forEach((other, otherIndex) => {
            if (otherIndex !== index && other.value !== value) other.value = value;
          });
          clearDiscountMessage();
        });
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            applyDiscount(input.value);
          }
        });
      });

      elements.discountApplyButtons.forEach((button) => {
        button.addEventListener('click', () => applyDiscount(elements.discountInputs[0]?.value || ''));
      });

      elements.discountRemoveButtons.forEach((button) => {
        button.addEventListener('click', () => removeDiscount());
      });
    }

    async function applyDiscount(rawCode, { silent = false } = {}) {
      const code = String(rawCode || '').trim();
      if (!code || state.discountApplying) {
        if (!code && !silent) setDiscountMessage('Въведете код за отстъпка.');
        return false;
      }
      if (!config.apiBaseUrl) {
        if (!silent) setDiscountMessage('Проверката на кодове временно не е достъпна.');
        return false;
      }

      state.discountApplying = true;
      elements.discountApplyButtons.forEach((button) => { button.disabled = true; button.textContent = '...'; });
      if (!silent) clearDiscountMessage();

      try {
        const values = getFormValues();
        const response = await fetch(`${config.apiBaseUrl.replace(/\/$/, '')}/checkout/discounts/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            email: values.email || '',
            deliveryType: state.deliveryType,
            cart: { currency: config.currency, lines: config.lines },
          }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body?.ok || !body?.discount) {
          throw new Error(body?.error?.message || 'Невалиден код за отстъпка.');
        }

        state.discount = body.discount;
        elements.discountInputs.forEach((input) => { input.value = state.discount.code; });
        elements.discountForms.forEach((formEl) => { formEl.hidden = true; });
        elements.discountToggles.forEach((toggle) => { toggle.hidden = true; });
        renderAppliedDiscount();
        updatePriceDisplay();
        // On mobile, reveal the order summary immediately after a valid code is
        // accepted so the customer can see the discount reflected in context.
        if (elements.mobileCartDetails && window.matchMedia('(max-width: 900px)').matches) {
          elements.mobileCartDetails.open = true;
        }
        if (!silent) setDiscountMessage('Кодът е приложен.', true);
        return true;
      } catch (error) {
        if (!silent) setDiscountMessage(error?.message || 'Невалиден код за отстъпка.');
        if (silent) removeDiscount(false);
        return false;
      } finally {
        state.discountApplying = false;
        elements.discountApplyButtons.forEach((button) => { button.disabled = false; button.textContent = 'Приложи'; });
      }
    }

    async function refreshAppliedDiscount() {
      if (!state.discount?.code) return;
      await applyDiscount(state.discount.code, { silent: true });
    }

    function removeDiscount(showForm = true) {
      state.discount = null;
      elements.discountRows.forEach((row) => { row.hidden = true; });
      elements.discountInputs.forEach((input) => { input.value = ''; });
      clearDiscountMessage();
      elements.discountToggles.forEach((toggle) => { toggle.hidden = !showForm; });
      elements.discountForms.forEach((formEl) => { formEl.hidden = showForm ? false : true; });
      updatePriceDisplay();
    }

    function renderAppliedDiscount() {
      if (!state.discount) {
        elements.discountRows.forEach((row) => { row.hidden = true; });
        return;
      }

      const label = state.discount.code ? `Код ${state.discount.code}` : (state.discount.title || 'Отстъпка');
      let amount;

      if (state.discount.kind === 'FREE_SHIPPING' && state.deliveryType === 'ADDRESS') {
        // Keep the accepted code remembered, but make it unambiguous that it is
        // currently dormant because free delivery is an OFFICE-only benefit.
        amount = 'Само до офис';
      } else {
        const itemDiscountCents = Number(state.discount.discountCents || 0);
        const shippingDiscountCents = state.discount.kind === 'FREE_SHIPPING' && state.deliveryType === 'OFFICE'
          ? Number(state.discount.shippingDiscountCents || 0)
          : 0;
        const totalDiscountCents = itemDiscountCents + shippingDiscountCents;
        amount = totalDiscountCents > 0 ? `-${formatMoney(totalDiscountCents, config.currency)}` : 'Приложен';
      }

      elements.discountLabels.forEach((el) => { el.textContent = label; });
      elements.discountAmounts.forEach((el) => { el.textContent = amount; });
      elements.discountRows.forEach((row) => { row.hidden = false; });
    }

    function setDiscountMessage(message, success = false) {
      elements.discountMessages.forEach((el) => {
        el.hidden = false;
        el.textContent = message;
        el.classList.toggle('is-success', success);
      });
    }

    function clearDiscountMessage() {
      elements.discountMessages.forEach((el) => {
        el.hidden = true;
        el.textContent = '';
        el.classList.remove('is-success');
      });
    }

    async function submitOrderWithRecovery(base, payload) {
      const maxAttempts = 6;
      let lastError = null;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const response = await fetch(`${base}/checkout/orders`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            credentials: 'omit',
            body: JSON.stringify(payload),
          });

          const body = await safeJson(response);
          if (response.ok && body?.ok) return body;

          if (response.status >= 500) {
            throw new CheckoutError('В момента имаме временен технически проблем с поръчките.', body, { outage: true });
          }

          const code = body?.error?.code || '';
          const message = body?.error?.message || body?.message || (typeof body?.error === 'string' ? body.error : '') || 'Не успяхме да създадем поръчката. Моля, опитайте отново.';

          if ((code === 'ORDER_PROCESSING' || code === 'ORDER_RESULT_UNKNOWN') && attempt < maxAttempts - 1) {
            await delay(1200 + attempt * 500);
            continue;
          }
          throw new CheckoutError(message, body);
        } catch (error) {
          if (error instanceof CheckoutError) throw error;
          lastError = error;
          if (attempt < 2) {
            await delay(1200 + attempt * 800);
            continue;
          }
          throw new CheckoutError('В момента имаме временен технически проблем с поръчките.', { cause: error }, { outage: true });
        }
      }

      throw new CheckoutError(lastError?.message || 'Не успяхме да потвърдим поръчката. Опитайте отново.');
    }


    function bindItemEditing() {
      root.addEventListener('input', (event) => {
        const input = event.target.closest('[data-cp-edit-property]');
        if (!input) return;
        const name = input.dataset.cpEditProperty || '';
        input.classList.remove('cp-input-invalid');
        input.setCustomValidity('');
        if (name === 'Голям надпис') {
          const start = input.selectionStart;
          const upper = input.value.toUpperCase();
          if (upper !== input.value) {
            input.value = upper;
            if (start !== null) input.setSelectionRange(start, start);
          }
        }
      });

      function formatUploadedFileName(fileName) {
        const raw = String(fileName || '').trim();
        if (!raw) return '';

        const lastDot = raw.lastIndexOf('.');
        const hasExtension = lastDot > 0 && lastDot < raw.length - 1;
        const extension = hasExtension ? raw.slice(lastDot) : '';
        const stem = hasExtension ? raw.slice(0, lastDot) : raw;
        const maxStemChars = 16;

        if (stem.length <= maxStemChars) return raw;
        return `${stem.slice(0, maxStemChars)}(...)${extension}`;
      }

      root.addEventListener('change', (event) => {
        const fileInput = event.target.closest('[data-cp-edit-image-input]');
        if (!fileInput) return;
        const item = fileInput.closest('.cp-order-item');
        if (!item) return;
        const file = fileInput.files && fileInput.files[0];
        const preview = item.querySelector('[data-cp-edit-image-preview]');
        const name = item.querySelector('[data-cp-edit-image-name]');
        if (!file) {
          resetEditImagePreview(item);
          return;
        }
        if (!file.type || !file.type.startsWith('image/')) {
          fileInput.value = '';
          const error = item.querySelector('[data-cp-edit-error]');
          if (error) {
            error.hidden = false;
            error.textContent = 'Моля, изберете валиден файл със снимка.';
          }
          resetEditImagePreview(item);
          return;
        }
        if (item.dataset.cpObjectUrl) URL.revokeObjectURL(item.dataset.cpObjectUrl);
        const objectUrl = URL.createObjectURL(file);
        item.dataset.cpObjectUrl = objectUrl;
        if (preview) preview.src = objectUrl;
        if (name) {
          name.textContent = formatUploadedFileName(file.name);
          name.title = file.name;
        }
        const error = item.querySelector('[data-cp-edit-error]');
        if (error) { error.hidden = true; error.textContent = ''; }
      });

      root.addEventListener('click', async (event) => {
        const openButton = event.target.closest('[data-cp-edit-open]');
        if (openButton) {
          const item = openButton.closest('.cp-order-item');
          if (!item) return;
          openItemEditor(item);
          return;
        }

        const cancelButton = event.target.closest('[data-cp-edit-cancel]');
        if (cancelButton) {
          const item = cancelButton.closest('.cp-order-item');
          if (!item) return;
          closeItemEditor(item, true);
          return;
        }

        const saveButton = event.target.closest('[data-cp-edit-save]');
        if (saveButton) {
          const item = saveButton.closest('.cp-order-item');
          if (!item) return;
          await saveItemEditor(item);
        }
      });
    }

    function findConfigLineByKey(key) {
      return (Array.isArray(config.lines) ? config.lines : []).find((line) => String(line.key) === String(key)) || null;
    }

    function openItemEditor(item) {
      const key = item.dataset.cartLineKey;
      const line = findConfigLineByKey(key);
      if (!line) return;

      item.querySelectorAll('[data-cp-edit-property]').forEach((input) => {
        const name = input.dataset.cpEditProperty;
        input.value = line.properties?.[name] ?? '';
      });

      item.querySelector('[data-cp-properties-view]')?.setAttribute('hidden', '');
      item.querySelector('[data-cp-edit-actions]')?.setAttribute('hidden', '');
      item.querySelector('[data-cp-editor]')?.removeAttribute('hidden');
      const error = item.querySelector('[data-cp-edit-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      resetEditImagePreview(item);
      item.querySelector('[data-cp-edit-property]')?.focus();
    }

    function resetEditImagePreview(item) {
      if (!item) return;
      if (item.dataset.cpObjectUrl) {
        URL.revokeObjectURL(item.dataset.cpObjectUrl);
        delete item.dataset.cpObjectUrl;
      }
      const key = item.dataset.cartLineKey;
      const line = findConfigLineByKey(key);
      const currentUrl = String(line?.properties?.['Снимка на колата'] || '');
      const input = item.querySelector('[data-cp-edit-image-input]');
      if (input) input.value = '';
      const preview = item.querySelector('[data-cp-edit-image-preview]');
      const link = item.querySelector('[data-cp-edit-image-link]');
      const name = item.querySelector('[data-cp-edit-image-name]');
      if (preview && currentUrl) preview.src = currentUrl;
      if (link && currentUrl) link.href = currentUrl;
      if (name) name.textContent = '';
      if (name) name.hidden = true;
    }

    function closeItemEditor(item, restoreValues = false) {
      const key = item.dataset.cartLineKey;
      const line = findConfigLineByKey(key);
      if (restoreValues && line) {
        item.querySelectorAll('[data-cp-edit-property]').forEach((input) => {
          const name = input.dataset.cpEditProperty;
          input.value = line.properties?.[name] ?? '';
        });
      }
      resetEditImagePreview(item);
      item.querySelector('[data-cp-editor]')?.setAttribute('hidden', '');
      item.querySelector('[data-cp-properties-view]')?.removeAttribute('hidden');
      item.querySelector('[data-cp-edit-actions]')?.removeAttribute('hidden');
    }

    function validateCarPosterEditInput(input) {
      const name = input.dataset.cpEditProperty || '';
      let value = String(input.value || '').trim();

      input.classList.remove('cp-input-invalid');
      input.setCustomValidity('');

      if (name === 'Голям надпис') {
        value = value.toUpperCase();
        input.value = value;
        if (!value) {
          input.setCustomValidity('Моля, въведете голям надпис.');
        } else if (value.length > 9) {
          input.setCustomValidity('Големият надпис може да бъде до 9 букви.');
        } else if (!/^[A-Z]+$/.test(value)) {
          input.setCustomValidity('Големият надпис трябва да съдържа само латински букви.');
        }
      } else if (name === 'Тел. номер') {
        // Product-page rule: (+359|0) followed by exactly 9 digits.
        // Be forgiving about spaces/dashes in checkout and normalize them away.
        value = value.replace(/[\s().-]+/g, '');
        input.value = value;
        if (!/^(?:\+359|0)[0-9]{9}$/.test(value)) {
          input.setCustomValidity('Въведете валиден телефонен номер, напр. 0888123456 или +359888123456.');
        }
      } else if (name === 'Спецификации') {
        if (!value) input.setCustomValidity('Моля, въведете спецификациите или NONE.');
      } else if (name === 'Две имена') {
        if (!value) input.setCustomValidity('Моля, въведете две имена.');
      }

      if (!input.checkValidity()) {
        input.classList.add('cp-input-invalid');
        return { valid: false, value };
      }
      return { valid: true, value };
    }

    function validateCarPosterItemEditor(item) {
      const values = {};
      let firstInvalid = null;
      item.querySelectorAll('[data-cp-edit-property]').forEach((input) => {
        const result = validateCarPosterEditInput(input);
        const name = input.dataset.cpEditProperty;
        if (name) values[name] = result.value;
        if (!result.valid && !firstInvalid) firstInvalid = input;
      });

      if (firstInvalid) {
        firstInvalid.reportValidity();
        firstInvalid.focus();
        return { valid: false, values: {} };
      }
      return { valid: true, values };
    }

    async function saveItemEditor(item) {
      const oldKey = item.dataset.cartLineKey;
      const line = findConfigLineByKey(oldKey);
      if (!line) return;

      const validation = validateCarPosterItemEditor(item);
      if (!validation.valid) return;

      const nextProperties = { ...(line.properties || {}) };
      Object.entries(validation.values).forEach(([name, value]) => {
        nextProperties[name] = value;
      });

      const saveButtons = [...root.querySelectorAll('.cp-order-item')]
        .filter((el) => el.dataset.cartLineKey === oldKey)
        .map((el) => el.querySelector('[data-cp-edit-save]'))
        .filter(Boolean);
      saveButtons.forEach((button) => {
        button.disabled = true;
        button.textContent = 'Запазваме...';
      });

      const errorEls = [...root.querySelectorAll('.cp-order-item')]
        .filter((el) => el.dataset.cartLineKey === oldKey)
        .map((el) => el.querySelector('[data-cp-edit-error]'))
        .filter(Boolean);
      errorEls.forEach((el) => { el.hidden = true; el.textContent = ''; });

      try {
        const imageInput = item.querySelector('[data-cp-edit-image-input]');
        const newImageFile = imageInput?.files?.[0] || null;
        let cart = null;
        let updatedItem = null;

        if (newImageFile) {
          const addUrl = (window.Shopify?.routes?.root || '/') + 'cart/add.js';
          const formData = new FormData();
          formData.append('id', String(line.variantId));
          formData.append('quantity', String(Number(line.quantity || 1)));

          Object.entries(nextProperties).forEach(([name, value]) => {
            if (name === 'Снимка на колата') return;
            formData.append(`properties[${name}]`, String(value ?? ''));
          });
          formData.append('properties[Снимка на колата]', newImageFile, newImageFile.name);

          const addResponse = await fetch(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' },
            body: formData,
          });
          const added = await addResponse.json().catch(() => null);
          if (!addResponse.ok || !added) {
            throw new Error(added?.description || added?.message || 'Не успяхме да качим новата снимка.');
          }

          const addedItem = Array.isArray(added?.items) ? added.items[0] : added;
          const addedKey = addedItem?.key;
          if (!addedKey) {
            throw new Error('Снимката беше качена, но не успяхме да обновим продукта. Опитайте отново.');
          }

          const cartChangeUrl = config.cartChangeUrl || '/cart/change.js';
          const removeResponse = await fetch(cartChangeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: oldKey, quantity: 0 }),
          });
          cart = await removeResponse.json().catch(() => null);

          if (!removeResponse.ok || !cart) {
            // Best-effort rollback: remove the newly-added replacement and leave the original untouched.
            try {
              await fetch(cartChangeUrl, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: addedKey, quantity: 0 }),
              });
            } catch (_) {}
            throw new Error(cart?.description || cart?.message || 'Не успяхме да заменим снимката. Опитайте отново.');
          }

          updatedItem = (cart.items || []).find((cartItem) => String(cartItem.key) === String(addedKey)) || null;
          if (!updatedItem) {
            throw new Error('Снимката е сменена, но визуализацията не се обнови. Затворете и отворете поръчката отново.');
          }
        } else {
          const cartChangeUrl = config.cartChangeUrl || '/cart/change.js';
          const response = await fetch(cartChangeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              id: oldKey,
              quantity: Number(line.quantity || 1),
              properties: nextProperties,
            }),
          });

          cart = await response.json().catch(() => null);
          if (!response.ok || !cart) {
            throw new Error(cart?.description || cart?.message || 'Не успяхме да запазим промените.');
          }
          updatedItem = findUpdatedCartItem(cart.items || [], line, nextProperties);
          if (!updatedItem) {
            throw new Error('Промените са запазени, но не успяхме да обновим визуализацията. Затворете и отворете поръчката отново.');
          }
        }

        const newKey = updatedItem.key || oldKey;
        const savedProperties = { ...(updatedItem.properties || nextProperties) };
        line.key = newKey;
        line.quantity = Number(updatedItem.quantity || line.quantity || 1);
        line.properties = savedProperties;

        updateLineCopies(oldKey, newKey, savedProperties);

        // A product-level discount may depend on the current cart line data.
        // Revalidate an applied code after an edit so totals never become stale.
        if (state.discount?.code) {
          await refreshAppliedDiscount();
        }
      } catch (error) {
        console.error('[CarPoster checkout] Cart item edit failed.', error);
        errorEls.forEach((el) => {
          el.hidden = false;
          el.textContent = error?.message || 'Не успяхме да запазим промените. Опитайте отново.';
        });
        return;
      } finally {
        saveButtons.forEach((button) => {
          button.disabled = false;
          button.textContent = 'ГОТОВО';
        });
      }
    }

    function findUpdatedCartItem(items, line, properties) {
      const expectedVariant = String(line.variantId || '');
      const expectedProps = normalizePropertyObject(properties);
      return items.find((item) => {
        const variantId = String(item.variant_id || item.variantId || item.id || '');
        if (expectedVariant && variantId !== expectedVariant) return false;
        return JSON.stringify(normalizePropertyObject(item.properties || {})) === JSON.stringify(expectedProps);
      }) || null;
    }

    function normalizePropertyObject(properties) {
      return Object.keys(properties || {})
        .sort()
        .reduce((result, key) => {
          result[key] = String(properties[key] ?? '');
          return result;
        }, {});
    }

    function updateLineCopies(oldKey, newKey, properties) {
      const copies = [...root.querySelectorAll('.cp-order-item')].filter((item) => item.dataset.cartLineKey === oldKey);
      copies.forEach((item) => {
        item.dataset.cartLineKey = newKey;
        item.querySelectorAll('[data-cp-property-row]').forEach((row) => {
          const name = row.dataset.propertyName;
          if (!name || !(name in properties)) return;
          const value = row.querySelector('[data-cp-property-value]');
          if (!value) return;
          const raw = String(properties[name] ?? '');
          if (raw.includes('/uploads/')) {
            const link = value.querySelector('a');
            const img = value.querySelector('img');
            if (link) link.href = raw;
            if (img) img.src = raw;
            return;
          }
          value.textContent = raw;
        });
        item.querySelectorAll('[data-cp-edit-property]').forEach((input) => {
          const name = input.dataset.cpEditProperty;
          if (name in properties) input.value = properties[name] ?? '';
        });
        const imageUrl = String(properties['Снимка на колата'] || '');
        if (imageUrl) {
          const preview = item.querySelector('[data-cp-edit-image-preview]');
          const link = item.querySelector('[data-cp-edit-image-link]');
          if (preview) preview.src = imageUrl;
          if (link) link.href = imageUrl;
        }
        closeItemEditor(item, false);
      });
    }

    function bindForm() {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (state.submitting) return;

        clearAllErrors();
        const values = getFormValues();
        const validation = validate(values);

        if (!validation.valid) {
          Object.entries(validation.errors).forEach(([field, message]) => setFieldError(field, message));
          focusFirstError(validation.errors);
          return;
        }

        if (!config.apiBaseUrl) {
          showFormError('Поръчките временно не са достъпни.');
          return;
        }

        setSubmitting(true);

        try {
          const payload = buildOrderPayload(values);
          const base = config.apiBaseUrl.replace(/\/$/, '');
          const body = await submitOrderWithRecovery(base, payload);

          const orderReference = body?.orderName || body?.orderNumber || body?.orderId || body?.id || '';
          const confirmationRef = body?.confirmationRef || '';
          const successUrl = body?.successUrl || config.successUrl;

          if (!successUrl) {
            throw new CheckoutError('Поръчката е създадена, но липсва страница за потвърждение.');
          }

          // Private, same-tab fallback. This is intentionally not put in the URL.
          // The secure confirmation endpoint remains the primary source of truth.
          try {
            sessionStorage.setItem('cp_last_order_fallback', JSON.stringify({
              orderName: orderReference,
              totalCents: Number(body?.totalCents || 0),
              currency: body?.currency || 'EUR',
              savedAt: Date.now()
            }));
          } catch (_) {}

          await clearShopifyCart();
          clearCheckoutId(config);

          const target = new URL(successUrl, window.location.origin);
          if (confirmationRef) target.searchParams.set('ref', String(confirmationRef));

          window.location.assign(target.toString());
        } catch (error) {
          console.error('[CarPoster checkout] Order submission error.', error);
          if (error?.outage) {
            showWorkerOutageError();
          } else {
            showFormError(error.message || 'Възникна проблем. Поръчката не е изгубена — опитайте отново.');
          }
          setSubmitting(false);
        }
      });
    }

    function getFormValues() {
      const data = new FormData(form);
      const rawPhone = String(data.get('phone') || '').trim();
      return {
        email: String(data.get('email') || '').trim(),
        firstName: String(data.get('firstName') || '').trim(),
        lastName: String(data.get('lastName') || '').trim(),
        phone: normalizeBulgarianPhone(rawPhone),
        rawPhone,
        emailMarketingOptOut: data.get('emailMarketingOptOut') === 'true',
        deliveryType: String(data.get('deliveryType') || state.deliveryType),
        paymentMethod: String(data.get('paymentMethod') || 'COD'),
        street: String(data.get('street') || '').trim(),
        streetNumber: String(data.get('streetNumber') || '').trim(),
        additionalInfo: String(data.get('additionalInfo') || '').trim(),
        manualOfficeText: elements.officeSearch?.value.trim() || '',
        manualCityText: elements.citySearch?.value.trim() || '',
      };
    }

    function validate(values) {
      const errors = {};

      if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = 'Моля, въведете валиден имейл.';
      if (values.firstName.length < 2) errors.firstName = 'Моля, въведете име.';
      if (values.lastName.length < 2) errors.lastName = 'Моля, въведете фамилия.';

      const phoneDigits = values.phone.replace(/\D/g, '');
      if (phoneDigits.length !== 12 || !values.phone.startsWith('+359')) {
        errors.phone = 'Моля, въведете валиден български телефон.';
      }

      if (values.deliveryType === 'OFFICE') {
        if (state.selectedOffice) {
          // A selected office is valid whether it came from the live lookup or
          // from the bundled fallback list.
        } else if (state.officeLookupUnavailable) {
          if (values.manualOfficeText.length < 3) errors.office = 'Моля, въведете град и офис на Еконт.';
        } else {
          errors.office = 'Моля, изберете офис на Еконт.';
        }
      } else if (values.deliveryType === 'ADDRESS') {
        if (state.cityLookupUnavailable) {
          if (values.manualCityText.length < 2) errors.city = 'Моля, въведете населено място.';
        } else if (!state.selectedCity) {
          errors.city = 'Моля, изберете населено място.';
        }
        if (values.street.length < 2) errors.street = 'Моля, въведете улица.';
        if (!values.streetNumber) errors.streetNumber = 'Въведете номер.';
      }

      return { valid: Object.keys(errors).length === 0, errors };
    }

    function getCheckoutTrackingSnapshot() {
      try {
        const attribution = window.CarPosterAttribution?.get?.();
        if (!attribution || typeof attribution !== 'object') return null;

        // Clone the snapshot so the checkout request receives plain JSON only.
        // Tracking is optional telemetry: any recorder/storage problem must never
        // prevent the customer from placing the order.
        return JSON.parse(JSON.stringify(attribution));
      } catch (_) {
        return null;
      }
    }


    function getCheckoutAnalyticsSnapshot() {
      try {
        const analytics = window.CarPosterAnalytics?.get?.();
        if (!analytics || typeof analytics !== 'object') return null;

        // GA4 measurement is optional telemetry just like attribution. A missing
        // cookie, consent API, or analytics helper must never block checkout.
        return JSON.parse(JSON.stringify(analytics));
      } catch (_) {
        return null;
      }
    }

    function buildOrderPayload(values) {
      const subtotalCents = Number(config.subtotalCents || 0);
      const thresholdCents = Number(config.freeShippingThresholdCents || 5140);
      const shippingCents = subtotalCents >= thresholdCents
        ? 0
        : (values.deliveryType === 'ADDRESS'
            ? Number(config.addressShippingCents || 0)
            : Number(config.officeShippingCents || 0));

      const delivery = values.deliveryType === 'OFFICE'
        ? (state.selectedOffice?.source === 'LOCAL_FALLBACK'
            ? {
                courier: 'ECONT',
                type: 'OFFICE',
                office: {
                  manualFallback: true,
                  manualText: [state.selectedOffice.name, state.selectedOffice.address, state.selectedOffice.code ? `код ${state.selectedOffice.code}` : ''].filter(Boolean).join(', '),
                },
              }
            : (state.selectedOffice
                ? {
                    courier: 'ECONT',
                    type: 'OFFICE',
                    office: {
                      providerId: state.selectedOffice.id,
                      providerCode: state.selectedOffice.code,
                      name: state.selectedOffice.name,
                      city: state.selectedOffice.city,
                      postalCode: state.selectedOffice.postalCode,
                      address: state.selectedOffice.address,
                      latitude: state.selectedOffice.latitude,
                      longitude: state.selectedOffice.longitude,
                    },
                  }
                : {
                    courier: 'ECONT',
                    type: 'OFFICE',
                    office: {
                      manualFallback: true,
                      manualText: values.manualOfficeText,
                    },
                  }))
        : (state.cityLookupUnavailable
            ? {
                courier: 'ECONT',
                type: 'ADDRESS',
                address: {
                  countryCode: 'BG',
                  manualFallback: true,
                  providerCityId: null,
                  city: values.manualCityText,
                  postalCode: null,
                  region: null,
                  providerStreetId: null,
                  street: values.street,
                  number: values.streetNumber,
                  additionalInfo: values.additionalInfo || null,
                },
              }
            : {
                courier: 'ECONT',
                type: 'ADDRESS',
                address: {
                  countryCode: 'BG',
                  providerCityId: state.selectedCity.id,
                  city: state.selectedCity.name,
                  postalCode: state.selectedCity.postalCode || null,
                  region: state.selectedCity.region || null,
                  providerStreetId: state.selectedStreet?.id || null,
                  street: state.selectedStreet?.name || values.street,
                  number: values.streetNumber,
                  additionalInfo: values.additionalInfo || null,
                },
              });

      return {
        version: CHECKOUT_VERSION,
        checkoutId: state.checkoutId,
        source: 'SHOPIFY_THEME',
        shop: config.shop,
        tracking: getCheckoutTrackingSnapshot() || undefined,
        analytics: getCheckoutAnalyticsSnapshot() || undefined,
        customer: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          emailMarketingOptOut: Boolean(values.emailMarketingOptOut),
          emailMarketingState: values.emailMarketingOptOut ? 'UNSUBSCRIBED' : 'SUBSCRIBED',
        },
        delivery,
        paymentMethod: 'COD',
        expectedTotals: {
          subtotalCents: Number(config.subtotalCents || 0),
          shippingCents,
          currency: config.currency,
        },
        // Free shipping is an OFFICE-only CarPoster benefit. Keep the code in
        // UI state when ADDRESS is selected, but do not submit it to Shopify for
        // an address order. Switching back to OFFICE reuses/revalidates it.
        discountCodes: state.discount?.code && !(state.discount.kind === 'FREE_SHIPPING' && state.deliveryType === 'ADDRESS')
          ? [state.discount.code]
          : [],
        cart: {
          token: config.cartToken || null,
          currency: config.currency,
          lines: config.lines,
        },
      };
    }

    function normalizeBulgarianPhone(value) {
      const digits = value.replace(/\D/g, '');
      if (!digits) return '';
      if (digits.startsWith('359')) return `+${digits}`;
      if (digits.startsWith('0')) return `+359${digits.slice(1)}`;
      return `+359${digits}`;
    }

    function updatePriceDisplay() {
      const subtotalCents = Number(config.subtotalCents || 0);
      const thresholdCents = Number(config.freeShippingThresholdCents || 5140);
      const shippingCents = subtotalCents >= thresholdCents
        ? 0
        : (state.deliveryType === 'ADDRESS'
            ? Number(config.addressShippingCents || 0)
            : Number(config.officeShippingCents || 0));
      const discountCents = Number(state.discount?.discountCents || 0);
      // Defensive UI rule: a free-shipping code can only affect OFFICE delivery.
      // Even if stale discount state exists for a moment, ADDRESS must always
      // keep its normal shipping price (unless the normal subtotal threshold
      // itself already makes shipping free).
      const shippingDiscountCents = state.deliveryType === 'OFFICE' && state.discount?.kind === 'FREE_SHIPPING'
        ? Number(state.discount?.shippingDiscountCents || 0)
        : 0;
      const finalShippingCents = Math.max(0, shippingCents - shippingDiscountCents);
      const totalCents = Math.max(0, subtotalCents - discountCents + finalShippingCents);

      const shippingText = finalShippingCents === 0 ? 'БЕЗПЛАТНА' : formatMoney(finalShippingCents, config.currency);
      const totalText = formatMoney(totalCents, config.currency);
      const totalBgnText = formatBgnFromEurCents(totalCents);

      elements.shipping.forEach((element) => {
        element.textContent = shippingText;
        element.classList.toggle('is-free', finalShippingCents === 0);
      });
      elements.totals.forEach((element) => { element.textContent = totalText; });
      elements.totalsBgn.forEach((element) => { element.textContent = totalBgnText; });
    }

    function formatMoney(cents, currency) {
      try {
        return new Intl.NumberFormat('bg-BG', {
          style: 'currency',
          currency: currency || 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
      }
    }

    function formatBgnFromEurCents(cents) {
      const eur = Number(cents || 0) / 100;
      const bgn = eur * 1.95583;
      try {
        return new Intl.NumberFormat('bg-BG', {
          style: 'currency',
          currency: 'BGN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(bgn);
      } catch (_) {
        return `${bgn.toFixed(2).replace('.', ',')} лв.`;
      }
    }

    function setFieldError(field, message) {
      const error = root.querySelector(`[data-error-for="${cssEscape(field)}"]`);
      if (!error) return;
      error.textContent = message;
      const fieldWrap = error.closest('.cp-field');
      fieldWrap?.classList.add('is-invalid');
      fieldWrap?.querySelector('input')?.setAttribute('aria-invalid', 'true');
    }

    function clearFieldError(field) {
      const error = root.querySelector(`[data-error-for="${cssEscape(field)}"]`);
      if (!error) return;
      error.textContent = '';
      const fieldWrap = error.closest('.cp-field');
      fieldWrap?.classList.remove('is-invalid');
      fieldWrap?.querySelector('input')?.removeAttribute('aria-invalid');
    }

    function clearAllErrors() {
      root.querySelectorAll('[data-error-for]').forEach((error) => {
        error.textContent = '';
        const fieldWrap = error.closest('.cp-field');
        fieldWrap?.classList.remove('is-invalid');
        fieldWrap?.querySelector('input')?.removeAttribute('aria-invalid');
      });
      clearFormError();
    }

    function focusFirstError(errors) {
      const firstField = Object.keys(errors)[0];
      if (!firstField) return;

      if (firstField === 'office') {
        elements.officeSearch?.focus();
        elements.officeSearch?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (firstField === 'city') {
        elements.citySearch?.focus();
        elements.citySearch?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const input = form.elements[firstField];
      if (input?.focus) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    function showFormError(message) {
      if (!elements.formError) return;
      elements.formError.textContent = message;
      elements.formError.hidden = false;
      elements.formError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function showWorkerOutageError() {
      if (!elements.formError) return;
      elements.formError.innerHTML = '';
      const title = document.createElement('strong');
      title.textContent = 'В момента имаме временен технически проблем с поръчките.';
      const text = document.createElement('span');
      text.append(' Моля, пишете ни във Viber на ');
      const link = document.createElement('a');
      link.href = 'viber://chat?number=%2B359876566333';
      link.textContent = '0876 566 333';
      link.rel = 'nofollow';
      text.append(link, ' и ще приемем поръчката Ви ръчно.');
      elements.formError.append(title, text);
      elements.formError.hidden = false;
      elements.formError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function clearFormError() {
      if (!elements.formError) return;
      elements.formError.textContent = '';
      elements.formError.hidden = true;
    }

    function setSubmitting(submitting) {
      state.submitting = submitting;
      if (!elements.submit) return;
      elements.submit.disabled = submitting;
      elements.submit.classList.toggle('is-loading', submitting);
      if (elements.submitSpinner) elements.submitSpinner.hidden = !submitting;
      if (elements.submitLabel) elements.submitLabel.textContent = submitting ? 'ИЗПРАЩАМЕ ПОРЪЧКАТА...' : 'ЗАВЪРШИ ПОРЪЧКАТА';
    }
  }

  function normalizeSearchText(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('bg-BG')
      .replace(/[.,()№#–—\-_/\\]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getCheckoutStorageKey(config) {
    const lines = Array.isArray(config.lines) ? config.lines : [];
    const fingerprint = lines.map((line) => `${line.key || line.variantId}:${line.quantity}`).join('|');
    return `carposterCheckout:${config.cartToken || 'cart'}:${fingerprint}`;
  }

  function getOrCreateCheckoutId(config) {
    const storageKey = getCheckoutStorageKey(config);
    try {
      const existing = sessionStorage.getItem(storageKey);
      if (existing) return existing;
      const id = window.crypto?.randomUUID?.() || `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem(storageKey, id);
      return id;
    } catch (_) {
      return window.crypto?.randomUUID?.() || `cp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  function clearCheckoutId(config) {
    try {
      sessionStorage.removeItem(getCheckoutStorageKey(config));
    } catch (_) {
      // The order is already created; storage cleanup is best-effort only.
    }
  }

  async function clearShopifyCart() {
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/clear.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      });
    } catch (error) {
      console.warn('[CarPoster checkout] Order created, but storefront cart could not be cleared.', error);
    }
  }

  async function safeJson(response) {
    try {
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  function cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  class CheckoutError extends Error {
    constructor(message, payload, options = {}) {
      super(message);
      this.name = 'CheckoutError';
      this.payload = payload;
      this.outage = Boolean(options.outage);
    }
  }
})();
